import moment from 'moment';

/**
 * THIN-8401: one existing payer of the patient, as `GET /provider/patient-payers/{patientUuid}/date-ranges`
 * returns it (yyyy-MM-dd, `endDate` null when open-ended).
 */
export type PayerDateRange = {
  startDate: string;
  endDate: string | null;
  uuid?: string;
};

type DateLike = Date | string | null | undefined;

const ISO_DAY = 'YYYY-MM-DD';

const toDay = (date: DateLike): string | null => {
  if (!date) return null;
  const parsed = typeof date === 'string' ? moment(date, ISO_DAY, true) : moment(date);
  return parsed.isValid() ? parsed.format(ISO_DAY) : null;
};

const covers = (range: PayerDateRange, day: string) =>
  range.startDate <= day && (range.endDate === null || day <= range.endDate);

const oldestFirst = (ranges: PayerDateRange[]) =>
  [...ranges].sort((a, b) => a.startDate.localeCompare(b.startDate));

/**
 * Whether a new payer may NOT start on `date`, mirroring `createPatientPayer`:
 * - a start inside any earlier payer overlaps it and is rejected;
 * - inside the latest payer, only a start after that payer's start is accepted (it ends the latest payer the day
 *   before, via the "Changing a Payer?" confirm); a start on the latest payer's start date is rejected.
 */
export const isNewPayerStartBlocked = (date: DateLike, ranges: PayerDateRange[]): boolean => {
  const day = toDay(date);
  if (!day || ranges.length === 0) return false;
  const sorted = oldestFirst(ranges);
  const latest = sorted[sorted.length - 1];
  if (day === latest.startDate) return true;
  return sorted.slice(0, -1).some((range) => covers(range, day));
};

/**
 * The last day a new payer starting on `startDate` may end on: the day before the next existing payer starts.
 * Null when there is no bound -- no start yet, no later payer, or a start after the latest payer's start (that
 * payer gets end-dated instead).
 */
export const newPayerMaxEndDate = (
  startDate: DateLike,
  ranges: PayerDateRange[]
): string | null => {
  const day = toDay(startDate);
  if (!day) return null;
  const next = oldestFirst(ranges).find((range) => range.startDate > day);
  return next ? moment(next.startDate, ISO_DAY).subtract(1, 'day').format(ISO_DAY) : null;
};
